import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Search,
  ShoppingBag,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { DebouncedInput } from '@/components/DebouncedInput';
import { useSellerCoverageItemsQuery } from '@/queries/useSellerCoverageReportQuery';
import { SellerCoverageItem } from '@/api/business/marketResearch';

interface SellerCoverageItemsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sellerData: SellerCoverageItem | null;
}

const SellerCoverageItemsDialog = ({
  isOpen,
  onClose,
  sellerData,
}: SellerCoverageItemsDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const payload = useMemo(() => {
    if (!sellerData || !sellerData.SK_PESQUISA || !sellerData.SK_VENDEDOR) {
      return null;
    }
    return {
      SK_PESQUISA: sellerData.SK_PESQUISA,
      SK_VENDEDOR: sellerData.SK_VENDEDOR,
    };
  }, [sellerData]);

  const { data: items, isLoading } = useSellerCoverageItemsQuery(payload);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (!searchTerm.trim()) return items;

    const term = searchTerm.toLowerCase();
    return items.filter((item) => {
      const clientMatch = item.DS_NOME_FANTASIA?.toLowerCase().includes(term);
      const productMatch = item.DS_PRODUTO?.toLowerCase().includes(term);
      const statusMatch = item.DD_STATUS?.toLowerCase().includes(term);
      const reasonMatch =
        item.DS_MOTIVO_NAO_PESQUISA?.toLowerCase().includes(term);
      return clientMatch || productMatch || statusMatch || reasonMatch;
    });
  }, [items, searchTerm]);

  // Summary stats
  const stats = useMemo(() => {
    if (!items) return { totalItems: 0, totalClients: 0, nonResearchCount: 0 };
    const uniqueClients = new Set(items.map((i) => i.SK_CLIENTE)).size;
    const nonResearch = items.filter((i) =>
      Boolean(i.DS_MOTIVO_NAO_PESQUISA),
    ).length;
    return {
      totalItems: items.length,
      totalClients: uniqueClients,
      nonResearchCount: nonResearch,
    };
  }, [items]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] flex flex-col p-0 overflow-hidden"
        aria-describedby={undefined}
      >
        {/* Header */}
        <DialogHeader className="flex flex-col gap-1 p-5 border-b bg-gray-50">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <ShoppingBag className="w-5 h-5 text-primary" />
            Itens Pesquisados Realizados
          </DialogTitle>
          {sellerData && (
            <DialogDescription className="text-sm text-gray-600">
              Vendedor:{' '}
              <span className="font-semibold text-gray-900">
                {sellerData.NM_FUNCIONARIO}
              </span>{' '}
              ({sellerData.DS_REVENDA}) • Pesquisa:{' '}
              <span className="font-semibold text-gray-900">
                {sellerData.NM_PESQUISA}
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Subheader / Summary Stats */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white border-b">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 font-semibold">
              <ShoppingBag className="w-4 h-4" />
              <span>{stats.totalItems} Produtos Pesquisados</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              <span>{stats.totalClients} Clientes Atendidos</span>
            </div>
            {stats.nonResearchCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 font-semibold">
                <AlertCircle className="w-4 h-4" />
                <span>{stats.nonResearchCount} Não Pesquisados</span>
              </div>
            )}
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
            <DebouncedInput
              value={searchTerm}
              onChange={(val) => setSearchTerm(String(val))}
              placeholder="Buscar cliente ou produto..."
              className="w-full text-xs border border-gray-200 rounded-md pl-9 bg-gray-50 h-9 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Body Table */}
        <div className="flex-1 p-4 overflow-y-auto bg-gray-50/50">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm font-medium">
                Carregando itens da pesquisa...
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              Nenhum item encontrado para esta pesquisa.
            </div>
          ) : (
            <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs min-w-[700px]">
                  <thead>
                    <tr className="font-semibold text-gray-700 border-b bg-gray-100/90">
                      <th className="p-3 pl-4 min-w-[180px]">Cliente</th>
                      <th className="p-3 min-w-[180px]">Produto</th>
                      <th className="p-3 text-right min-w-[100px]">
                        Preço Compra
                      </th>
                      <th className="p-3 text-center min-w-[80px]">Volume</th>
                      <th className="p-3 text-right min-w-[100px]">
                        Preço Venda
                      </th>
                      <th className="p-3 min-w-[140px]">Status / Motivo</th>
                      <th className="p-3 pr-4 text-right min-w-[100px]">
                        Data
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredItems.map((item, idx) => {
                      const isNonResearch = Boolean(
                        item.DS_MOTIVO_NAO_PESQUISA,
                      );
                      return (
                        <tr
                          key={idx}
                          className="transition-colors hover:bg-gray-50/80"
                        >
                          <td className="p-3 pl-4 font-semibold text-gray-900">
                            {item.DS_NOME_FANTASIA}
                          </td>
                          <td className="p-3 font-medium text-gray-800">
                            {item.DS_PRODUTO}
                          </td>
                          <td className="p-3 font-mono text-right text-gray-700">
                            {isNonResearch
                              ? '-'
                              : item.VL_PRECO_COMPRA !== null
                                ? `R$ ${Number(item.VL_PRECO_COMPRA).toFixed(2)}`
                                : '-'}
                          </td>
                          <td className="p-3 font-mono text-center text-gray-700">
                            {isNonResearch
                              ? '-'
                              : (item.QTD_VOLUME_COMPRA ?? '-')}
                          </td>
                          <td className="p-3 font-mono text-right text-gray-700">
                            {isNonResearch
                              ? '-'
                              : item.VL_PRECO_VENDA !== null
                                ? `R$ ${Number(item.VL_PRECO_VENDA).toFixed(2)}`
                                : '-'}
                          </td>
                          <td className="p-3">
                            {isNonResearch ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">
                                <AlertCircle className="w-3 h-3" />
                                {item.DS_MOTIVO_NAO_PESQUISA}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                <CheckCircle2 className="w-3 h-3" />
                                {item.DD_STATUS || 'Publicado'}
                              </span>
                            )}
                          </td>
                          <td className="p-3 pr-4 font-mono text-right text-gray-500">
                            {item.DT_PUBLICACAO
                              ? new Date(item.DT_PUBLICACAO).toLocaleDateString(
                                  'pt-BR',
                                )
                              : item.CREATED_AT
                                ? new Date(item.CREATED_AT).toLocaleDateString(
                                    'pt-BR',
                                  )
                                : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SellerCoverageItemsDialog;
